import {type ActionResponse, ActionService, MissionOneMidRequest} from "./actionService";
import {QueryParameters} from "../../../common/queryParameters";
import {resolve} from "../../../container";
import {EopErrorHandler} from "../../../common/errorHandler";
import {UnLitElement, WithEopSlots} from "../../../common/elements";
import {customElement, property, query, state} from "lit/decorators.js";
import type {Nullable} from "../../../bootstrap/common/types";
import {html, type TemplateResult} from "lit";
import type {EopSpinner} from "../../../page/elements/spinner";
import type {PropertyValues} from "@lit/reactive-element";

const MID_PARAM_NAME = "mid";
const CONFIRMED_STATE_LABEL = "confirmed";
const ERROR_STATE_LABEL = "error";

export abstract class EopMissionOneAction extends WithEopSlots(UnLitElement) {

    @property({attribute: "confirm-text-slot-name"})
    private confirmTextSlotName: string;
    @property({attribute: "confirmed-text-slot-name"})
    private confirmedTextSlotName: string;
    @property({attribute: "error-text-slot-name"})
    private errorTextSlotName: string;

    @state()
    private activeSlot: Nullable<string>;

    @query("eop-spinner")
    private spinner: EopSpinner;

    protected constructor(
        protected actionService: ActionService = resolve(ActionService),
        private queryParameters: QueryParameters = resolve(QueryParameters),
        private errorHandler: EopErrorHandler = resolve(EopErrorHandler)
    ) {
        super();

        this.activeSlot = null;
    }

    public firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        this.execute();
    }

    public execute(): void {
        const mid = this.queryParameters.getString(MID_PARAM_NAME);

        if (!mid || mid === "" || mid === ERROR_STATE_LABEL) {
            this.showErrorText();
            return;
        }
        if (mid === CONFIRMED_STATE_LABEL) {
            this.showConfirmedText();
            return;
        }

        void this.spinner.spinWhile(this.executeAction(new MissionOneMidRequest(mid))
            .then((response: ActionResponse) => {
                if (response.success) {
                    this.showConfirmText();
                    this.queryParameters.set(MID_PARAM_NAME, CONFIRMED_STATE_LABEL);
                } else {
                    this.showErrorText();
                    this.queryParameters.set(MID_PARAM_NAME, ERROR_STATE_LABEL);
                }
            }).catch(error => {
                this.showErrorText();
                this.queryParameters.set(MID_PARAM_NAME, ERROR_STATE_LABEL);
                this.errorHandler.report(error);
            }));
    }

    protected abstract executeAction(request: MissionOneMidRequest): Promise<ActionResponse>;

    private showConfirmText(): void {
        this.activeSlot = this.confirmTextSlotName;
    }

    private showConfirmedText(): void {
        this.activeSlot = this.confirmedTextSlotName;
    }

    private showErrorText(): void {
        this.activeSlot = this.errorTextSlotName;
    }

    public render(): Nullable<TemplateResult> {
        return html`
            <eop-spinner></eop-spinner>
            ${this.renderActiveSlot()}
        `;
    }

    private renderActiveSlot(): Nullable<TemplateResult> {
        if (!this.activeSlot) {
            return null;
        }

        return html`
            ${this.resolveSlot(this.activeSlot)}
        `;
    }
}

@customElement("eop-confirm-mission-one-subscriber")
export class EopConfirmMissionOneSubscriber extends EopMissionOneAction {

    public executeAction(request: MissionOneMidRequest): Promise<ActionResponse> {
        return this.actionService.confirmMissionOneSubscriber(request);
    }
}

@customElement("eop-delete-mission-one-subscriber")
export class EopDeleteMissionOneSubscriber extends EopMissionOneAction {

    public executeAction(request: MissionOneMidRequest): Promise<ActionResponse> {
        return this.actionService.deleteMissionOneSubscriber(request);
    }
}